import { Link } from "@inertiajs/react";
import { TrendingUp, BookOpen, Medal } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import StatTile from "@/components/shared/StatTile";
import ParentActions from "@/components/parents/ParentActions";

// Sample props for the real components on a child's page.
const SAMPLE_STUDENT = {
  first_name: "Sarah",
  shareable: true,
  public_page_url: "https://pagepledge.com/p/dyq8s4-bear-moon-42",
  public_page_path: "#",
  log_minutes_path: "#",
};
const SAMPLE_CAMPAIGN = { finished: false, minutes_state: "open" };

function ChildPagePreview() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatTile size="sm" icon={TrendingUp} label="Raised" value="$75" color="primary" />
        <StatTile size="sm" icon={BookOpen} label="Minutes" value="135" color="forest" />
        <StatTile size="sm" icon={Medal} label="Class rank" value="2nd" hint="by minutes" />
      </div>
      <ParentActions student={SAMPLE_STUDENT} schoolName="Lincoln Elementary" campaign={SAMPLE_CAMPAIGN} />
    </div>
  );
}

export default function ParentDashboard() {
  return (
    <>
      <p>
        After you activate your child, you land on their page. It's where you do everything: log reading from home, share their page with family, and see how they, their class and the school are doing.
      </p>

      <h2>The two things you'll do most</h2>
      <Screenshot caption="The top of your child's page">
        <ChildPagePreview />
      </Screenshot>
      <p>
        <strong>Log reading minutes</strong> opens a quick form for reading done at home — see <Link href="/help/articles/logging-minutes-at-home">Logging reading from home</Link>. Before reading starts, and after the last morning to log, the card says when logging opens or closed.
      </p>
      <p>
        <strong>Share donation page</strong> opens your phone's share menu (or email and text on a computer), your child's link to copy, and a few messages you can paste. Anyone you send it to can donate without making an account — see <Link href="/help/articles/sharing-with-family">Sharing with family</Link>. Sharing opens when donations open, about two weeks before reading starts.
      </p>

      <h2>Further down the page</h2>
      <p>
        Set a <Link href="/help/articles/setting-personal-goals">goal with your child</Link>, see the prizes they can earn, how their class and school are doing, every donation that's come in (with messages), and every reading log. You can fix or undo reading you logged yourself; entries from teachers and other family members are marked as theirs.
      </p>

      <h2>More than one child?</h2>
      <p>
        Choose <strong>Add another child</strong> in the menu and type their activation code. No email link this time — you're already signed in. On a phone, switch between children with the names under the top bar. See <Link href="/help/articles/multiple-children">Adding more than one child</Link>.
      </p>

      <Callout variant="tip" title="Pin PagePledge to your home screen">
        <p>
          On most phones, you can "Add to home screen" from your browser's share menu. It works just like an app — no install, no app store. Handy if you log minutes every day.
        </p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/logging-minutes-at-home">Logging reading from home</Link>.</li>
        <li><Link href="/help/articles/sharing-with-family">Sharing with family</Link>.</li>
        <li><Link href="/help/articles/setting-personal-goals">Setting a personal goal</Link>.</li>
      </ul>
    </>
  );
}
